import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { ChatbotService } from '../../../services/chatbot-services/chatbot.service';
import { SelectedModuleService } from '../../../services/selected-module-service/selected-module.service';
import { environment } from 'src/environments/environment';
import { LanguageChangeService } from 'src/services/language-change/language-change.service';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from 'src/services/logger-services/logger.service';
import { DataSetVdb } from '../../models/chatbot.models';

/**
 * IntroWizardComponent displays a wizard interface that
 * lets the user know about Freya which can be also viewed anytime by the user.
 */
@Component({
  selector: 'app-intro-wizard',
  templateUrl: './intro-wizard.component.html',
  styleUrls: ['./intro-wizard.component.css']
})
export class IntroWizardComponent implements OnInit {

  /** Emits when the wizard is closed */
  @Output() close = new EventEmitter<void>();

  /** Tracks which wizard steps (accordion panels) are open */
  isOpen = [true, false, false];

  /** Configuration data fetched from the chatbot service */
  configdata: any;

  /** The currently selected domain/dataset name */
  selectedDomainName: any;

  /** List of available allowed vector database datasets */
  vectorDbDatasets: DataSetVdb[] = [];

  /** User ID of the user */
  UserID: any;

  /** Domain ID of the user */
  DomainID: any;

  /** Error template for INTRO WIZARD */
  private readonly introWizardError = (error: any) => 
    `[INTRO WIZARD (?)] - ${JSON.stringify(error)}`;


  constructor(
    private readonly chatbotService: ChatbotService,
    private readonly selectedModuleService: SelectedModuleService,
    private readonly languageService: LanguageChangeService,
    private readonly translate: TranslateService,
    private readonly loggerService: LoggerService
  ) {}

  /**
   * Lifecycle hook that runs on component initialization.
   * Initializes config data and available vector DB datasets.
   */
  ngOnInit() {
    try {
      this.UserID = environment.LoginUserID;
      this.DomainID = environment.APPModuleID;
      this.getConfigData();
      this.initializeVectorDbDatasets();
    } catch (error) {
      this.loggerService.logError(this.introWizardError(error), "ngOnInit");
    }
  }

  /**
   * Fetches configuration data from the chatbot service
   * and sets the intro wizard Introduction slide content accordingly.
   */
  getConfigData() {
    try {
      this.configdata = this.chatbotService.getConfigData();
    } catch (error) {
      this.loggerService.logError(this.introWizardError(error), "getConfigData");
    }
  }

  /**
   * Initializes the available and allowed vector DB datasets
   * from the global environment configuration.
   */
  public initializeVectorDbDatasets(): void {
    try {
      if (environment && environment.GlobalInfo["vectorDbDatasets"]?.length > 0) {
        this.vectorDbDatasets = environment.GlobalInfo["vectorDbDatasets"];
        this.subscribeToSelectedModule();
      }
    } catch (error) {
      this.loggerService.logError(this.introWizardError(error), "initializeVectorDbDatasets");
    }
  }

  /**
   * Subscribes to changes in the selected module service,
   * sets the current selected domain, and ensures default is selected.
   */
  public subscribeToSelectedModule(): void {
    try {
      this.selectedModuleService.currentSelectedValue.subscribe(value => {
        if (value) {
          // Set the selected domain from the shared service
          this.selectedDomainName = value.selectedVectorDbDataset;
        } else {
          // Set the first dataset if none is selected
          const defaultValue = this.vectorDbDatasets.length > 0 ? this.vectorDbDatasets[0] : null;
          this.selectedDomainName = defaultValue;
          this.selectedModuleService.changeSelectedValue({ selectedVectorDbDataset: defaultValue });
        }
        this.getConfigData(); // Refresh config based on selection
      });
    } catch (error) {
      this.loggerService.logError(this.introWizardError(error), "subscribeToSelectedModule");
    }
  }

  /**
   * Toggles the visibility of the wizard accordion panel.
   * @param index Index of the panel to toggle
   */
  toggle(index: number): void {
    this.isOpen[index] = !this.isOpen[index];
  }

  /**
   * Emits the `close` event to notify parent component to close the wizard.
   */
  closeWizard() {
    this.close.emit();
  }
}
